import {BasicFunnelStep, FunnelStep, IncompleteBuckets, TrendUnit} from 'common/report/models/funnels/types';
import {FunnelBookmark} from 'common/report/models/funnels/types';

export interface BasicQueryApiParams {
  bookmark: FunnelBookmark;
  funnel_id: number;
  /** format should be CSV or undefined */
  format?: string;
  query_type: string;
  /** title is available when downloading PDF/CSV, else undefined */
  title?: string;
  use_query_cache: boolean;
}

export interface ApiParams extends BasicQueryApiParams {
  projectId: string;
  workspaceId?: string;
}

interface BasicQueryResponse {
  computed_at?: string;
  min_sampling_factor?: number;
  meta?: any;
  data?: any;
  error?: string;
  message?: string;
}

interface ErrorResponse extends BasicQueryResponse {
  error: string;
  message?: string;
}

interface Analysis {
  completion: number;
  starting_amount: number;
  steps: number;
  worst: number;
}

/** A nested map of segments, with Array<FunnelStep> leaf nodes. */
export interface SegmentedFunnelResults {
  $overall?: Array<BasicFunnelStep | FunnelStep>;
  [segment: string]: SegmentedFunnelResults | Array<BasicFunnelStep | FunnelStep>;
}

export interface UnsegmentedFunnelResult {
  analysis: Analysis;
  steps: BasicFunnelStep[];
}

export type KeyedByDate<T> = {
  [date: string]: T;
};

export interface RegularQueryResponse extends BasicQueryResponse {
  data: {
    [date: string]: UnsegmentedFunnelResult;
  };
  compare_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };
}

export interface SegmentedQueryResponse extends BasicQueryResponse {
  data: {
    [date: string]: SegmentedFunnelResults;
  };

  compare_data?: {
    [date: string]: SegmentedFunnelResults;
  };

  meta: {
    property_values: string[];
    dates: string[];
  };
}

export interface TrendsQueryResponse extends BasicQueryResponse {
  meta: {
    dates: string[];
    // TODO (lkloh): Do not use, we will remove this once the trend_unit is deleted from the backend response.
    trend_unit: TrendUnit;
    incomplete_buckets: IncompleteBuckets;
  };
  data: {
    [date: string]: UnsegmentedFunnelResult;
  };

  compare_data?: {
    [date: string]: UnsegmentedFunnelResult;
  };
}

export interface SegmentedTrendsQueryResponse extends BasicQueryResponse {
  meta: {
    property_values: string[];
    dates: string[];
    incomplete_buckets: IncompleteBuckets;
  };
  data: {
    [date: string]: {
      [segment: string]: Array<BasicFunnelStep | FunnelStep>;
    };
  };

  compare_data?: {
    [date: string]: SegmentedFunnelResults;
  };
}

export enum SankeyNodeType {
  Anchor = `ANCHOR`,
  Dropoff = `DROPOFF`,
  Pruned = `PRUNED`,
  Event = `EVENT`,
}

export interface SankeyEdge {
  breakdowns: any;
  event: string;
  is_computed: boolean;
  segments: string[];
  total_count: number;
  type: SankeyNodeType;
}

export interface SankeyNode {
  breakdowns: any;
  edges: SankeyEdge[];
  event: string;
  is_computed: boolean;
  segments: string[];
  total_count: number;
  type: SankeyNodeType;
}

export interface SankeyStep {
  nodes: SankeyNode[];
}

export interface SankeyData {
  breakdowns: any;
  steps: SankeyStep[];
}

export interface SankeyResponse extends BasicQueryResponse {
  meta: {
    min_sampling_factor: number;
  };
  data: {
    [date: string]: SankeyData;
  };
}

export interface FlowsResponse extends BasicQueryResponse {
  metadata: {
    min_sampling_factor: number;
  };
  breakdowns: any;
  steps: SankeyStep[];
}

export type FunnelQueryResponse = RegularQueryResponse | SegmentedQueryResponse | TrendsQueryResponse;

export type QueryResponse = FunnelQueryResponse | SankeyResponse | ErrorResponse;

export enum RequestStatus {
  Main = `request_status_data`,
  Trends = `request_status_trend`,
  Sankey = `request_status_sankey`,
}

export enum RequestState {
  Loading = `loading`,
  Complete = `complete`,
  Error = `error`,
}

export enum QueryType {
  Main = `funnels_report`,
  Trends = `funnels_report_segmented_trends`,
  TimeToConvert = `funnels_report_time_to_convert`,
  Sankey = `funnels_any_order_sankey`,
  DropOff = `flows`,
  Frequency = `funnels_report_frequency`,
}

export function isErrorResponse(r: QueryResponse): r is ErrorResponse {
  return `error` in r;
}

export function isSegmented(r: QueryResponse): r is SegmentedQueryResponse | SegmentedTrendsQueryResponse {
  const firstDataKey = Object.keys(r.data)[0];
  return !(`analysis` in r.data[firstDataKey]);
}

export function isSegmentedData(r: UnsegmentedFunnelResult | SegmentedFunnelResults): r is SegmentedFunnelResults {
  return !(`analysis` in r);
}

export function isSankeyResponse(q: QueryType, _r: QueryResponse): _r is SankeyResponse {
  return q === QueryType.Sankey;
}

export function isTrendsResponse(q: QueryType, _r: QueryResponse): _r is TrendsQueryResponse {
  return q === QueryType.Trends;
}

export function isRegularResponse(q: QueryType, _r: QueryResponse): _r is RegularQueryResponse {
  return q === QueryType.Main;
}

export function isTimeToConvertResponse(q: QueryType, _r: QueryResponse): _r is RegularQueryResponse {
  return q === QueryType.TimeToConvert;
}

export function isFrequencyResponse(q: QueryType, _r: QueryResponse): _r is RegularQueryResponse {
  return q === QueryType.Frequency;
}

export function isFunnelStepArray(d: SegmentedFunnelResults['segment']): d is Array<BasicFunnelStep | FunnelStep> {
  return Array.isArray(d);
}
